import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { PrintRubricDocument } from '../studio/print/PrintRubricDocument';
import { buildCourseDashboardPath, buildCourseRubricPath } from '../studio/constants/courseStudioRoutes';
import { EXAM_LEVEL_OPTIONS } from '../studio/constants/examAiParams';
import { getSubjectById } from '../studio/constants/subjectCatalog';
import { getCourseProfileById } from '../studio/services/courseProfileStorage';
import { getRubric } from '../studio/services/rubricStorage';
import { useSalaAuth } from '../auth/SalaAuthContext';
import { resolveSalaTeacherDisplayName } from '../auth/resolveTeacherDisplayName';
import { resolveCourseDisplayName, resolveCourseSubjectTags } from '../studio/constants/courseProfileUi';

export function RubricPrintPage() {
  const { courseId = '', id: rubricId = '' } = useParams<{ courseId: string; id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, user } = useSalaAuth();
  const course = getCourseProfileById(courseId);
  const rubric = getRubric(rubricId);
  const subject = course ? getSubjectById(course.subjectId) : undefined;
  const teacherName = resolveSalaTeacherDisplayName(profile, user);
  const courseLevelLabel = course
    ? EXAM_LEVEL_OPTIONS.find((option) => option.id === course.level)?.label
    : undefined;

  const projectRoles = rubric?.projectRoles ?? [];
  const roleParam = searchParams.get('rol') || 'all';
  const [roleFilter, setRoleFilter] = useState<string>(roleParam);

  useEffect(() => {
    setRoleFilter(roleParam);
  }, [roleParam]);

  const isValid =
    course &&
    rubric &&
    rubric.courseId === courseId &&
    rubric.hasMatrix &&
    rubric.criteria.length > 0;

  useEffect(() => {
    document.body.setAttribute('data-print-document', 'true');
    return () => {
      document.body.removeAttribute('data-print-document');
    };
  }, []);

  useEffect(() => {
    if (searchParams.get('auto') === '1' && isValid) {
      const timer = window.setTimeout(() => window.print(), 400);
      return () => window.clearTimeout(timer);
    }
  }, [isValid, searchParams]);

  const roleOptions = useMemo(
    () => [{ id: 'all', name: 'Vista maestra (todos)' }, ...projectRoles],
    [projectRoles],
  );

  if (!isValid) {
    return (
      <div className="print-page-shell">
        <div className="print-page-toolbar no-print">
          <Link to={courseId ? buildCourseDashboardPath(courseId) : '/studio'} className="print-toolbar-btn">
            ← Volver
          </Link>
        </div>
        <div className="print-page-empty">
          <p>No se encontró la rúbrica o aún no tiene matriz para imprimir.</p>
          {courseId && rubricId ? (
            <Link to={buildCourseRubricPath(courseId, rubricId)} className="print-toolbar-btn print-toolbar-btn--primary">
              Editar rúbrica
            </Link>
          ) : (
            <Link to="/studio" className="print-toolbar-btn print-toolbar-btn--primary">
              Volver al Studio
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="print-page-shell print-page-shell--rubric">
      <div className="print-page-toolbar no-print">
        <Link to={buildCourseDashboardPath(courseId)} className="print-toolbar-btn">
          ← {resolveCourseDisplayName(course)}
        </Link>
        <div className="print-page-toolbar__actions">
          {projectRoles.length > 0 ? (
            <label className="inline-flex items-center gap-2 text-xs">
              Exportar
              <select
                className="rounded border px-2 py-1 text-xs"
                value={roleFilter}
                onChange={(event) => {
                  const next = event.target.value;
                  setRoleFilter(next);
                  const params = new URLSearchParams(searchParams);
                  if (next === 'all') params.delete('rol');
                  else params.set('rol', next);
                  setSearchParams(params, { replace: true });
                }}
              >
                {roleOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <Link to={buildCourseRubricPath(courseId, rubricId)} className="print-toolbar-btn">
            Editar rúbrica
          </Link>
          <button type="button" className="print-toolbar-btn print-toolbar-btn--primary" onClick={() => window.print()}>
            Imprimir / Guardar PDF
          </button>
        </div>
      </div>

      <PrintRubricDocument
        rubric={rubric}
        courseDisplayName={resolveCourseDisplayName(course)}
        courseLevelLabel={courseLevelLabel}
        courseLevelId={course.level}
        subjectName={subject?.name}
        courseSubjectTag={course.subjectTag || course.subjectId}
        courseAdditionalSubjectTags={resolveCourseSubjectTags(course).filter(
          (tag) => tag !== String(course.subjectTag || course.subjectId || '').trim().toLowerCase(),
        )}
        schoolName={course.schoolName || profile?.schoolName}
        schoolLogoDataUrl={course.schoolLogoDataUrl}
        teacherName={teacherName}
        roleFilter={roleFilter === 'all' ? 'all' : roleFilter}
      />
    </div>
  );
}
